import { Button, Flex } from 'front-commons/ds';
import { useEffect, useMemo } from 'react';
import { DistributorsProps } from 'services/products/interfaces';
import { PromotionSuggestionCardProps } from '../interfaces';
import useOrderImport from 'stores/orderImport';
import ItemSuggestionCard from './ItemSuggestionCard';
import UndoSuggestionCard from './UndoSuggestionCard';

export default function PromotionSuggestionCard({
	posId,
	loading,
	itemState,
	suggestion,
	showReplaceButton,
	simpleProductInfo,
	hasOnlyOneSuggestion,
	updateSummary,
	onUndoTimeout,
	promotionHandlers,
	handleChangeProductType,
}: PromotionSuggestionCardProps) {
	const { handleRemovePosSinglePromotion, handleAddPosPromotions } = useOrderImport();

	const selectedDistributor = useMemo(
		() =>
			suggestion.distributors.find(
				({ distributorId }) => distributorId === simpleProductInfo.currentDistributorId,
			) as DistributorsProps,
		[],
	);

	const handleReplaceClick = (noFetch = false) => {
		const { get: updatedData, map } = promotionHandlers(simpleProductInfo.id);

		const products = map.productsInCombo(suggestion.productsInCombo, updatedData.quantity, suggestion.fixedAmount);

		const commonPayload = {
			distributorId: selectedDistributor.distributorId,
			quantity: suggestion.fixedAmount ? updatedData.quantity : 1,
		};

		handleAddPosPromotions({
			posId,
			promotionId: suggestion.id,
			payload: {
				orderImportProductId: '',
				products,
				...commonPayload,
			},
		});

		handleChangeProductType({
			changeInto: 'PROMOTION',
			data: {
				removeProductId: simpleProductInfo.id,
				addProduct: {
					productId: suggestion.id,
					productsInCombo: products,
					...commonPayload,
				},
			},
			noFetch,
		});
	};

	const handleUndoClick = () => {
		handleRemovePosSinglePromotion({
			posId,
			promotionId: suggestion.id,
		});

		handleChangeProductType({
			changeInto: 'SIMPLE',
			data: {
				addProduct: {
					productId: simpleProductInfo.id,
					distributorId: simpleProductInfo.currentDistributorId,
					quantity: simpleProductInfo.quantity,
				},
				removeProductId: suggestion.id,
			},
		});
	};

	const handleUndoTimeout = () => {
		onUndoTimeout?.(simpleProductInfo.id);
	};

	useEffect(() => {
		updateSummary(simpleProductInfo.id, { currentDistributorId: selectedDistributor.distributorId });
		promotionHandlers(simpleProductInfo.id).set.distributorId(selectedDistributor.distributorId);
	}, [selectedDistributor]);

	return (
		<Flex direction="column" gap="24px" width="100%">
			<ItemSuggestionCard
				posId={posId}
				promotionData={suggestion}
				updateSummary={updateSummary}
				promotionHandlers={promotionHandlers}
				simpleProductData={simpleProductInfo}
				selectedDistributor={selectedDistributor}
				onAfterReplaceSourceProduct={() => {
					handleReplaceClick(true);
				}}
			/>

			{itemState === 'PARTIALLY_CHANGED' && (
				<UndoSuggestionCard onUndo={handleUndoClick} onTimeout={handleUndoTimeout} loading={loading} />
			)}

			{itemState === 'READY' && (
				<>
					<ItemSuggestionCard posId={posId} simpleProductData={simpleProductInfo} />
					{showReplaceButton && !hasOnlyOneSuggestion && (
						<Flex justifyContent="flex-end" margin="-8px 0 0 0">
							<Button
								onClick={() => handleReplaceClick()}
								disabled={loading}
								loading={loading}
								minWidth="175px"
								variant="outlined"
							>
								Substituir por promoção
							</Button>
						</Flex>
					)}
				</>
			)}
		</Flex>
	);
}
